import PropTypes from 'prop-types';
import { FUNCTIONAL_COLORS, getSubjectColorIndex } from '../../../core/theme/palette';
import { CourseMotif } from '../../course-backgrounds';
import './primitives.css';

/**
 * IconTile
 *
 * A glyph centred in a 12%-tint rounded square, coloured from the functional
 * palette. `docs/design-language.md` §2 calls this "the single highest-leverage
 * visual change — it is what makes a list of rows look designed instead of
 * typed", and §4 fixes its two uses: a tile *leads* something (a navigational
 * row, a service entry, a stat), while a bare stroke glyph sits inline beside
 * text. Never both in the same place.
 *
 * Colour comes from a tone name or from a course identifier:
 *
 *   <IconTile icon={<IconCourse />} tone="saffron" />
 *   <IconTile icon={<IconCourse />} subject={course.id} />   // deterministic
 *
 * `subject` wins when both are given, and routes through the same hash the
 * timetable and the phone use, so Matematika is one colour everywhere.
 *
 * The tile is decorative by default: it repeats a label that is already beside
 * it, so it is hidden from screen readers unless given its own `label`. That is
 * also why colour is never the only thing it carries.
 */
const SEMANTIC_TONES = ['primary', 'neutral', 'success', 'warning', 'danger', 'info'];
export const ICON_TILE_TONES = [...FUNCTIONAL_COLORS, ...SEMANTIC_TONES];

const IconTile = ({
  icon,
  tone = 'neutral',
  subject,
  bgKey,
  size = 'sm',
  variant = 'tint',
  label,
  className = '',
}) => {
  const hasSubject = subject !== undefined && subject !== null && subject !== '';
  const toneClass = hasSubject
    ? `lms-icon-tile--subject-${getSubjectColorIndex(subject)}`
    : `lms-icon-tile--${tone}`;

  return (
    <span
      className={[
        'lms-icon-tile',
        `lms-icon-tile--${size}`,
        toneClass,
        variant === 'solid' ? 'lms-icon-tile--solid' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
    >
      {/* The course's chosen subject art as a faint same-hue watermark under the
          glyph — the same catalogue art the timetable and courses page use. A
          course with no chosen background renders nothing, so the tile is then
          exactly its tinted self. */}
      {bgKey && <CourseMotif themeKey={bgKey} className="lms-icon-tile__art" />}
      {icon}
    </span>
  );
};

IconTile.propTypes = {
  icon: PropTypes.node.isRequired,
  /** Functional-palette hue, or a semantic alias onto one of them. */
  tone: PropTypes.oneOf(ICON_TILE_TONES),
  /** Course id, code or name — colours the tile deterministically. */
  subject: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Course background catalogue key — draws the subject art as a watermark. */
  bgKey: PropTypes.string,
  /** sm 32px (rows, bands), md 40px (stats, service entries), lg 56px (states). */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** `tint` is the default 12% fill; `solid` is for the one tile that leads a hero. */
  variant: PropTypes.oneOf(['tint', 'solid']),
  /** Only when the tile carries meaning no adjacent text does. */
  label: PropTypes.string,
  className: PropTypes.string,
};

export default IconTile;
